/**
 * RTK Query endpoints for Check-in/Check-out.
 *
 * Injected into the shared `attendanceApi` via `injectEndpoints()`.
 * This file is imported as a side-effect in `store.ts` to guarantee
 * endpoints are registered before any component mounts.
 *
 * @module checkin-checkout/api/checkinCheckoutEndpoints
 */

import { attendanceApi } from "../../attendance/api/attendanceApiBase";
import type { CheckInOutStudent } from "../types/checkinout";
import type {
  AttendanceTodayData,
  DailyLogEntry,
} from "../data/attendanceDataAdapter";
import {
  loadAttendanceList,
  loadAttendanceTodayClean,
  loadAttendanceStudentDetail,
  addStudentNote,
  applyAttendanceAction,
  fetchAbsenceNotesForDate as fetchAbsenceNotesForCheckIn,
  enrichStudentsWithAbsenceNotes,
} from "../data/attendanceDataAdapter";
import { mapTodayDataToStudent } from "../data/attendanceTodayMapper";
import { fetchSchoolClasses, supabase } from "../../../lib/supabase";
import type {
  AttendanceListArgs,
  AttendanceActionArgs,
  AttendanceAddNoteArgs,
  UndoCheckoutStudentArgs,
  CheckInOutBootstrapArgs,
  CheckInOutBootstrapResult,
  CheckInOutClass,
} from "../../attendance/api/attendanceApiBase";

// Re-export types for convenience
export type {
  AttendanceListArgs,
  DailyLogEntry,
} from "../../attendance/api/attendanceApiBase";

// ============================================================================
// Helpers (copied from attendanceApi — used only by CheckInOut bootstrap)
// ============================================================================

function sortClassesNaturally(list: CheckInOutClass[]): CheckInOutClass[] {
  const parse = (name: string) => {
    const match = (name || "").trim().match(/^(\d+)([a-zA-Z]*)$/);
    if (match) {
      return {
        num: parseInt(match[1], 10),
        letter: (match[2] || "").toLowerCase(),
      };
    }
    return { num: Number.MAX_SAFE_INTEGER, letter: (name || "").toLowerCase() };
  };
  return [...list].sort((a, b) => {
    const A = parse(a.name);
    const B = parse(b.name);
    if (A.num !== B.num) return A.num - B.num;
    return A.letter.localeCompare(B.letter, "de");
  });
}

// ============================================================================
// Injected Endpoints
// ============================================================================

export const checkinCheckoutApi = attendanceApi.injectEndpoints({
  endpoints: (builder) => ({
    // ========================================================================
    // Today's Attendance
    // ========================================================================

    getAttendanceTodayClean: builder.query<
      AttendanceTodayData[],
      { schoolId: string | null }
    >({
      async queryFn(args) {
        try {
          if (!args.schoolId) return { data: [] };
          const rows = await loadAttendanceTodayClean();
          const data = rows
            .filter((row) => row.school_id === args.schoolId)
            .map((row) => row.data);
          return { data };
        } catch (error) {
          return { error };
        }
      },
      providesTags: (_r, _e, args) => [
        {
          type: "AttendanceList",
          id: `${args.schoolId ?? "none"}:${new Date()
            .toISOString()
            .slice(0, 10)}`,
        },
        { type: "AttendanceList", id: "TODAY_CLEAN" },
      ],
    }),

    getStudentDetailByDate: builder.query<
      CheckInOutStudent | null,
      { schoolId: string | null; studentId: string | null; date?: string | null }
    >({
      async queryFn(args) {
        try {
          if (!args.schoolId || !args.studentId) return { data: null };
          const row = await loadAttendanceStudentDetail(
            args.schoolId,
            args.studentId,
            args.date,
          );
          if (!row) return { data: null };
          return { data: mapTodayDataToStudent(row.data) };
        } catch (error) {
          return { error };
        }
      },
      providesTags: (_r, _e, args) => [
        { type: "StudentAttendance", id: args.studentId || "none" },
      ],
    }),

    getAttendanceList: builder.query<CheckInOutStudent[], AttendanceListArgs>({
      async queryFn(args) {
        try {
          const shouldLoad =
            !!args.schoolId &&
            (args.forceAll ||
              args.classIds?.length > 0 ||
              args.search?.trim().length > 0);
          if (!shouldLoad) return { data: [] };
          const { students } = await loadAttendanceList({
            schoolId: args.schoolId ?? "",
            date: new Date(args.date.slice(0, 10)),
            classIds: args.classIds,
            search: args.search.trim(),
            forceAll: args.forceAll,
            presentOnly: args.presentOnly,
          });
          return { data: students };
        } catch (error) {
          return { error };
        }
      },
      providesTags: (result, _e, args) => {
        const tags: {
          type: "AttendanceList" | "StudentAttendance";
          id: string;
        }[] = [
          {
            type: "AttendanceList",
            id: `${args.schoolId ?? "none"}:${args.date.slice(0, 10)}`,
          },
          { type: "AttendanceList", id: "all" },
        ];
        result?.forEach((s) =>
          tags.push({ type: "StudentAttendance", id: s.id }),
        );
        return tags;
      },
    }),

    getCheckInOutBootstrap: builder.query<
      CheckInOutBootstrapResult,
      CheckInOutBootstrapArgs
    >({
      async queryFn(args) {
        try {
          const { schoolId, date } = args;
          if (!schoolId) {
            return {
              data: {
                classes: [],
                students: [],
                timetableEvents: [],
                flexLessons: [],
                flexModules: [],
                lessonAttendanceByLessonId: {},
                discrepancyNotesByStudent: {},
                dailyLogByStudent: {},
              },
            };
          }

          const day = date.slice(0, 10);
          const isToday = new Date().toISOString().slice(0, 10) === day;
          let classes: CheckInOutClass[] = [];
          let students: CheckInOutStudent[] = [];
          const dailyLogByStudent: Record<string, DailyLogEntry> = {};

          if (isToday) {
            const rows = await loadAttendanceTodayClean();
            const bySchool = rows.filter(
              (r) => String(r.school_id) === String(schoolId),
            );
            const classMap = new Map<string, string>();
            students = bySchool.map((row) => {
              const data = row.data as unknown as Record<string, unknown>;
              if (data.class_id && data.class_name)
                classMap.set(String(data.class_id), String(data.class_name));
              return mapTodayDataToStudent(row.data);
            });
            classes = sortClassesNaturally(
              Array.from(classMap.entries()).map(([id, name]) => ({
                id,
                name,
              })),
            );
            // Enrich with absence notes from student_absence_notes
            const todayStr = new Date().toISOString().slice(0, 10);
            const absNotes = await fetchAbsenceNotesForCheckIn(
              schoolId,
              todayStr,
            );
            students = enrichStudentsWithAbsenceNotes(students, absNotes);
          } else {
            const classesRaw = await fetchSchoolClasses(schoolId);
            classes = sortClassesNaturally(
              (classesRaw || []).map((c: { id: string; name?: string }) => ({
                id: String(c.id),
                name: String(c.name || ""),
              })),
            );
            const result = await loadAttendanceList({
              schoolId,
              date: new Date(day),
              classIds: [],
              search: "",
              forceAll: true,
              presentOnly: false,
            });
            students = result.students;
            result.dailyLogByStudent.forEach((entry, sid) => {
              dailyLogByStudent[sid] = entry;
            });
          }

          return {
            data: {
              classes,
              students,
              timetableEvents: [],
              flexLessons: [],
              flexModules: [],
              lessonAttendanceByLessonId: {},
              discrepancyNotesByStudent: {},
              dailyLogByStudent,
            },
          };
        } catch (error) {
          return { error };
        }
      },
    }),

    // ========================================================================
    // Attendance Actions
    // ========================================================================

    applyAttendanceAction: builder.mutation<void, AttendanceActionArgs>({
      async queryFn(args) {
        try {
          await applyAttendanceAction(args);
          return { data: undefined };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: (_r, _e, args) =>
        args.studentIds.map((id) => ({
          type: "StudentAttendance" as const,
          id,
        })),
    }),

    addNote: builder.mutation<void, AttendanceAddNoteArgs>({
      async queryFn({ studentId, noteText }) {
        try {
          await addStudentNote(studentId, noteText);
          return { data: undefined };
        } catch (error) {
          return { error };
        }
      },
      invalidatesTags: (_r, _e, arg) => [
        { type: "StudentAttendance", id: arg.studentId },
        { type: "AttendanceList", id: "all" },
      ],
    }),

    undoCheckoutStudent: builder.mutation<
      DailyLogEntry | null,
      UndoCheckoutStudentArgs
    >({
      async queryFn({ schoolId, date, studentId, performedBy }) {
        try {
          // schoolId / performedBy retained as preconditions (caller must
          // be signed in with a valid school context), but NOT forwarded
          // to the RPC: the function derives school_id from
          // auth.get_user_school_id() and last_updated_by from
          // auth.uid(). Sending them would be a client-supplied-actor
          // smell and currently causes PGRST202.
          if (!schoolId || !studentId || !date || !performedBy)
            throw new Error("Missing required parameters");
          const { data, error } = await supabase.rpc(
            "ops_undo_checkout_student_react" as never,
            {
              p_student_id: studentId,
              p_date: date.slice(0, 10),
            } as never,
          );
          if (error) return { error };
          const row = (Array.isArray(data) ? data[0] : data) as Record<
            string,
            unknown
          > | null;
          if (!row) return { data: null };
          return {
            data: {
              studentId,
              date: date.slice(0, 10),
              presenceStatus: row.presence_status as string,
              checkInTime: row.check_in_time as string,
              checkOutTime: row.check_out_time as string,
              expectedArrivalTime: row.expected_arrival_time as string,
              expectedCheckoutTime: row.expected_checkout_time as string,
              notes: row.notes as string,
              checkOutBy: row.check_out_by as string,
              checkOutMethod: row.check_out_method as string,
            },
          };
        } catch (error) {
          return { error };
        }
      },
    }),
  }),
  overrideExisting: false,
});

// ============================================================================
// Generated Hooks
// ============================================================================

export const {
  useGetAttendanceTodayCleanQuery,
  useGetStudentDetailByDateQuery,
  useLazyGetStudentDetailByDateQuery,
  useGetAttendanceListQuery,
  useGetCheckInOutBootstrapQuery,
  useApplyAttendanceActionMutation,
  useAddNoteMutation,
  useUndoCheckoutStudentMutation,
} = checkinCheckoutApi;
